import { useEffect, useState } from 'react';
import { useAppSelector } from './useAppSelector';
import { selectUser } from '../store/features/auth';
import { hasCreatorPortalRole } from '../utils/creatorPortalAccess';
import {
  CREATOR_ONBOARDING_PATH,
  CREATOR_PATHS,
  resolveCreatorStudioPath,
} from '../config/creatorProgramPaths';
import { creatorService } from '../services/api/creator.service';

export function useCreatorStudioEntry() {
  const user = useAppSelector(selectUser);
  const isCreator = hasCreatorPortalRole(user?.roles);
  const [onboardingComplete, setOnboardingComplete] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!isCreator) {
      setOnboardingComplete(null);
      setLoading(false);
      return;
    }
    let mounted = true;
    setLoading(true);
    creatorService
      .getOnboardingStatus()
      .then(async (data: { completed?: boolean }) => {
        if (!mounted) return;
        if (data.completed) {
          setOnboardingComplete(true);
          return;
        }
        try {
          const profile = await creatorService.getProfile();
          if (mounted) setOnboardingComplete(Boolean(profile.onboardingCompletedAt));
        } catch {
          if (mounted) setOnboardingComplete(false);
        }
      })
      .catch(async () => {
        if (!mounted) return;
        try {
          const profile = await creatorService.getProfile();
          if (mounted) setOnboardingComplete(Boolean(profile.onboardingCompletedAt));
        } catch {
          if (mounted) setOnboardingComplete(false);
        }
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [isCreator, user?.id]);

  const creatorStudioPath = resolveCreatorStudioPath(onboardingComplete);

  return {
    isCreator,
    onboardingComplete,
    onboardingLoading: loading,
    creatorStudioPath,
    creatorOnboardingPath: CREATOR_ONBOARDING_PATH,
    creatorDashboardPath: CREATOR_PATHS.dashboard,
  };
}
